import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, devCountdown } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { emptyOrdersMessage } from "@/lib/orderUtils";
import { toast } from "sonner";
import ManualOrderModal from "@/components/orders/ManualOrderModal";
import BulkDeleteModal from "@/components/orders/BulkDeleteModal";
import ArchiveOldModal from "@/components/orders/ArchiveOldModal";
import RecoverOverridesModal from "@/components/orders/RecoverOverridesModal";
import OrdersHeaderActions from "@/components/orders/OrdersHeaderActions";
import OrdersFiltersBar from "@/components/orders/OrdersFiltersBar";
import OrdersTable from "@/components/orders/OrdersTable";
import OrdersPagination from "@/components/orders/OrdersPagination";
import OrdersBulkBar from "@/components/orders/OrdersBulkBar";
import OrderCardMobile from "@/components/orders/OrderCardMobile";

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [marketplaceFilter, setMarketplaceFilter] = useState("all");
  const [skuFilter, setSkuFilter] = useState("all");
  const [skuOptions, setSkuOptions] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [dateField, setDateField] = useState("order_date");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [purging, setPurging] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");  // shop.code recém-copiado
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [marketplaceCounts, setMarketplaceCounts] = useState({ all: 0, shopee: 0, mercado_livre: 0, bling_direto: 0 });
  const { canAdmin, hasPerm } = useCanAdmin();
  const canViewNfe = hasPerm ? (hasPerm("nfe_view") || hasPerm("fiscal")) : false;

  const copyShopCode = async (code) => {
    if (!code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        // Fallback para contexto não seguro (http)
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopiedCode(code);
      toast.success(`Copiado: ${code}`);
      setTimeout(() => setCopiedCode((c) => (c === code ? "" : c)), 1500);
    } catch (e) {
      console.warn("Falha ao copiar:", e?.message);
      toast.error("Não foi possível copiar");
    }
  };

  const buildFilterParams = () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (search) params.search = search;
    if (dateStart || dateEnd) {
      params.date_field = dateField;
      if (dateStart) params.date_start = dateStart;
      if (dateEnd) params.date_end = dateEnd;
    }
    if (marketplaceFilter !== "all") params.marketplace = marketplaceFilter;
    if (skuFilter !== "all") params.sku = skuFilter;
    if (showDeleted) params.deleted = true;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...buildFilterParams(), skip: (page - 1) * pageSize, limit: pageSize };
      const { data } = await api.get("/orders", { params });
      setOrders(data.items || []);
      setTotal(data.total || 0);
      setMarketplaceCounts(data.marketplace_counts || { all: 0, shopee: 0, mercado_livre: 0, bling_direto: 0 });
      setSelectedIds([]);
    } catch (e) {
      console.warn("Orders load failed:", e?.response?.status || e?.message);
    } finally { setLoading(false); }
  };

  // `search` e datas são aplicadas manualmente (Enter/botão Buscar/Aplicar) —
  // não devem re-disparar o load a cada tecla digitada.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter, page, pageSize, marketplaceFilter, skuFilter, showDeleted]);

  // Opções do dropdown de SKU (auto-populadas 1x)
  useEffect(() => {
    api.get("/orders/skus")
      .then(({ data }) => setSkuOptions(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/orders/export/excel", { params: buildFilterParams(), responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pedidos_${new Date().toLocaleDateString("en-CA")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado com os filtros atuais.");
    } catch (e) {
      console.warn("Export excel failed:", e?.response?.status || e?.message);
      toast.error("Falha ao exportar Excel.");
    } finally { setExporting(false); }
  };

  // Filtro por marketplace é derivado das observações do Bling, portanto aplicado no cliente.
  // "Atrasado" = qualquer condição de urgência crítica: dev passou dos 5 dias
  //   OU exit_date <= próximo dia útil (contando somente dias úteis: se hoje
  //   é sexta, o próximo dia útil é segunda-feira).
  const isOrderOverdue = (o) => {
    if (o.production_status === "enviado") return false;
    // 1) Dev overdue
    const dev = devCountdown(o);
    if (dev.applies && dev.days_overdue > 0) return true;
    // 2) Prazo de envio (exit_date) — atrasado ou dentro do próximo dia útil
    if (o.exit_date) {
      const exit = o.exit_date.slice(0, 10);
      const today = new Date();
      // Calcula próximo dia útil (pula sábado/domingo)
      let nb = new Date(today);
      nb.setDate(nb.getDate() + 1);
      while (nb.getDay() === 0 || nb.getDay() === 6) {
        nb.setDate(nb.getDate() + 1);
      }
      const nbStr = nb.toLocaleDateString("en-CA");
      if (exit <= nbStr) return true;
    }
    return false;
  };

  const overdueCount = orders.filter(isOrderOverdue).length;
  const filteredOrders = orders.filter((o) => !showOverdueOnly || isOrderOverdue(o));

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else load();
  };

  const clearDateFilter = () => {
    setDateStart("");
    setDateEnd("");
    setTimeout(() => (page !== 1 ? setPage(1) : load()), 0);
  };

  const sync = async () => {
    setSyncing(true);
    try { await api.post("/bling/sync-now"); setTimeout(load, 1500); }
    catch (e) { alert(e?.response?.data?.detail || "Configure e conecte o Bling primeiro"); }
    finally { setSyncing(false); }
  };

  const purgeCancelled = async () => {
    if (!confirm("Isso vai varrer todos os pedidos NÃO enviados e remover os que foram cancelados ou excluídos no Bling.\n\nO processo roda em background e pode levar 5–10 minutos. Continuar?")) return;
    setPurging(true);
    try {
      await api.post("/bling/purge-cancelled");
      alert("Varredura iniciada em background. A lista será atualizada em alguns minutos — se quiser acompanhar, veja os logs do backend.");
      setTimeout(load, 90 * 1000);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao iniciar varredura");
    } finally {
      setPurging(false);
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedIds.length === orders.length) setSelectedIds([]);
    else setSelectedIds(orders.map((o) => o.id));
  };

  const doBulkDelete = async () => {
    setDeleting(true);
    try {
      await api.post("/orders/bulk-delete", { ids: selectedIds });
      setConfirmDelete(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao excluir pedidos");
    } finally { setDeleting(false); }
  };

  const toggleDeletedView = () => {
    setShowDeleted((v) => !v);
    setSelectedIds([]);
    setShowOverdueOnly(false);
    setPage(1);
  };

  const restoreOrder = async (id) => {
    setRestoringId(id);
    try {
      await api.post(`/orders/${id}/restore`);
      toast.success("Pedido restaurado ao sistema.");
      await load();
    } catch (e) {
      if (e?.response?.status !== 428) {
        toast.error(e?.response?.data?.detail || "Erro ao restaurar pedido");
      }
    } finally { setRestoringId(null); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500 mb-2">GESTÃO DE PEDIDOS</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">{showDeleted ? "Pedidos Excluídos" : "Pedidos"}</h1>
          {showDeleted && (
            <div className="text-xs text-zinc-500 mt-1" data-testid="deleted-view-hint">
              Pedidos excluídos não aparecem no sistema nem voltam pelo sync do Bling. Restaure com a senha do colaborador.
            </div>
          )}
        </div>
        <OrdersHeaderActions
          showDeleted={showDeleted}
          onToggleDeleted={toggleDeletedView}
          showOverdueOnly={showOverdueOnly}
          onToggleOverdue={() => setShowOverdueOnly((v) => !v)}
          overdueCount={overdueCount}
          exporting={exporting}
          onExport={exportExcel}
          canAdmin={canAdmin}
          onRecover={() => setShowRecover(true)}
          onArchive={() => setShowArchive(true)}
          onManual={() => setShowManual(true)}
          purging={purging}
          onPurge={purgeCancelled}
          syncing={syncing}
          onSync={sync}
        />
      </div>

      <OrdersFiltersBar
        search={search} setSearch={setSearch}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        skuFilter={skuFilter} onSkuChange={(v) => { setSkuFilter(v); setPage(1); }} skuOptions={skuOptions}
        dateField={dateField} setDateField={setDateField}
        dateStart={dateStart} setDateStart={setDateStart}
        dateEnd={dateEnd} setDateEnd={setDateEnd}
        onApply={applyFilters} onClearDates={clearDateFilter}
        marketplaceFilter={marketplaceFilter}
        onMarketplaceChange={(k) => { setMarketplaceFilter(k); setPage(1); }}
        marketplaceCounts={marketplaceCounts}
      />

      {/* Desktop: tabela tradicional */}
      <OrdersTable
        loading={loading}
        orders={filteredOrders}
        ordersCount={orders.length}
        canAdmin={canAdmin}
        canViewNfe={canViewNfe}
        selectedIds={selectedIds}
        onToggleOne={toggleOne}
        onToggleAll={toggleAll}
        onOpen={(id) => navigate(`/orders/${id}`)}
        copiedCode={copiedCode}
        onCopyShopCode={copyShopCode}
        marketplaceFilter={marketplaceFilter}
        showDeleted={showDeleted}
        onRestore={restoreOrder}
        restoringId={restoringId}
      />

      {/* Mobile: cards empilhados */}
      <div className="md:hidden space-y-3" data-testid="orders-cards-mobile">
        {loading && (
          <div className="text-center py-10 text-zinc-400 border border-zinc-200 bg-white">
            Carregando...
          </div>
        )}
        {!loading && filteredOrders.length === 0 && (
          <div className="text-center py-10 border border-zinc-200 bg-white">
            <div className="overline text-zinc-400 mb-2">{showDeleted ? "SEM PEDIDOS EXCLUÍDOS" : "SEM PEDIDOS"}</div>
            <div className="text-zinc-500 text-sm px-4">
              {emptyOrdersMessage(showDeleted, marketplaceFilter)}
            </div>
          </div>
        )}
        {!loading &&
          filteredOrders.map((o) => (
            <OrderCardMobile
              key={o.id}
              order={o}
              selected={selectedIds.includes(o.id)}
              canAdmin={canAdmin}
              onToggleSelect={toggleOne}
              onOpen={(id) => navigate(`/orders/${id}`)}
              copiedCode={copiedCode}
              onCopyShopCode={copyShopCode}
              showDeleted={showDeleted}
              onRestore={restoreOrder}
              restoring={restoringId === o.id}
            />
          ))}
      </div>

      <OrdersPagination
        total={total} page={page} totalPages={totalPages}
        rangeStart={rangeStart} rangeEnd={rangeEnd}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPage(1); setPageSize(n); }}
        setPage={setPage}
        loading={loading}
      />

      {confirmDelete && (
        <BulkDeleteModal
          count={selectedIds.length}
          deleting={deleting}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={doBulkDelete}
        />
      )}

      {showManual && (
        <ManualOrderModal
          onClose={() => setShowManual(false)}
          onCreated={() => { setShowManual(false); load(); }}
        />
      )}
      {showArchive && (
        <ArchiveOldModal
          onClose={() => setShowArchive(false)}
          onArchived={(n) => { setShowArchive(false); load(); alert(`${n} pedido(s) arquivado(s) com sucesso.`); }}
        />
      )}
      {showRecover && (
        <RecoverOverridesModal onClose={() => { setShowRecover(false); load(); }} />
      )}

      {selectedIds.length > 0 && !showDeleted && (
        <OrdersBulkBar
          selectedIds={selectedIds}
          canAdmin={canAdmin}
          onClear={() => setSelectedIds([])}
          onDelete={() => setConfirmDelete(true)}
        />
      )}
    </div>
  );
}
